import {Component, DestroyRef, Input, OnChanges, SimpleChanges,} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule
} from 'ng-apexcharts';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatest} from 'rxjs';
import {PhotographerViewStatistics, PhotographerViewStatPoint,} from '../../entities/dashboard/view-stats.entity';
import {
  SegmentedControlComponent,
  SegmentedControlOption,
} from '../../../core/components/segmented-control/segmented-control.component';
import {InfoTooltipComponent} from '../../../core/components/info-tooltip/info-tooltip.component';

type TimeframeOption = 'daily' | 'monthly';
type MetricOption = 'all' | 'search' | 'profile';

type ChartOptions = {
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  fill: ApexFill;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  grid: ApexGrid;
  markers: ApexMarkers;
  colors: string[];
};

@Component({
  selector: 'app-view-stats',
  standalone: true,
  imports: [
    NgApexchartsModule,
    TranslocoPipe,
    SegmentedControlComponent,
    InfoTooltipComponent,
  ],
  templateUrl: './view-stats.component.html',
  styleUrl: './view-stats.component.scss'
})
export class ViewStatsComponent implements OnChanges {
  @Input() public stats: PhotographerViewStatistics|null = null;
  @Input() public loading = false;
  @Input() public error: string|null = null;

  public timeframe: TimeframeOption = 'daily';
  public metric: MetricOption = 'all';
  public timeframeControlOptions: SegmentedControlOption[] = [];
  public metricControlOptions: SegmentedControlOption[] = [];

  public chartSeries: ApexAxisChartSeries = [];
  public chartOptions: ChartOptions = this.createBaseChartOptions();

  private searchLabel = '';
  private profileLabel = '';

  public constructor(
    private readonly transloco: TranslocoService,
    destroyRef: DestroyRef,
  ) {
    this.initializeTranslations(destroyRef);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['stats']) {
      this.updateChart();
    }
  }

  public setTimeframe(option: TimeframeOption): void {
    if (this.timeframe === option) {
      return;
    }
    this.timeframe = option;
    this.updateChart();
  }

  public setMetric(option: MetricOption): void {
    if (this.metric === option) {
      return;
    }
    this.metric = option;
    this.updateChart();
  }

  public get hasData(): boolean {
    const series = this.getActiveSeries();
    return series.length > 0 && series.some((point) => {
      return (point.searchImpressions ?? 0) > 0 || (point.profileViews ?? 0) > 0;
    });
  }

  public get isEmpty(): boolean {
    return !this.loading && !this.error && !this.hasData;
  }

  public get totalSearchViews(): number | null {
    const value = this.stats?.total?.searchImpressions;
    if (value == null) {
      return null;
    }
    return this.normalizeValue(value);
  }

  public get totalProfileViews(): number | null {
    const value = this.stats?.total?.profileViews;
    if (value == null) {
      return null;
    }
    return this.normalizeValue(value);
  }

  public onTimeframeChange(option: string | number): void {
    this.setTimeframe(option as TimeframeOption);
  }

  public onMetricChange(option: string | number): void {
    this.setMetric(option as MetricOption);
  }

  private initializeTranslations(destroyRef: DestroyRef): void {
    const timeframe$ = combineLatest([
      this.transloco.selectTranslate('creator.dashboard.views.timeframe.daily'),
      this.transloco.selectTranslate('creator.dashboard.views.timeframe.monthly'),
    ]);

    const metric$ = combineLatest([
      this.transloco.selectTranslate('creator.dashboard.views.metric.all'),
      this.transloco.selectTranslate('creator.dashboard.views.metric.search'),
      this.transloco.selectTranslate('creator.dashboard.views.metric.profile'),
    ]);

    const labels$ = combineLatest([
      this.transloco.selectTranslate('creator.dashboard.views.cards.search'),
      this.transloco.selectTranslate('creator.dashboard.views.cards.profile'),
    ]);

    combineLatest([timeframe$, metric$, labels$])
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(([[daily, monthly], [all, searchMetric, profileMetric], [searchLabel, profileLabel]]) => {
        this.timeframeControlOptions = [
          {label: daily, value: 'daily'},
          {label: monthly, value: 'monthly'},
        ];

        this.metricControlOptions = [
          {label: all, value: 'all'},
          {label: searchMetric, value: 'search'},
          {label: profileMetric, value: 'profile'},
        ];

        this.searchLabel = searchLabel;
        this.profileLabel = profileLabel;
        this.updateChart();
      });
  }

  private createBaseChartOptions(): ChartOptions {
    return {
      chart: {
        type: 'area',
        height: 320,
        width: '100%',
        parentHeightOffset: 0,
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          speed: 400,
        },
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      fill: {
        type: 'gradient',
        gradient: {
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        categories: [],
        labels: {
          style: {
            colors: '#6B7280',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: this.createBaseYAxisOptions(),
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'dark',
        y: {
          formatter: (value?: number) => this.formatYAxisValue(value),
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
      },
      grid: {
        strokeDashArray: 4,
      },
      markers: {
        size: 3,
        strokeWidth: 1,
      },
      colors: ['#2556cc', '#b96300'],
    };
  }

  private updateChart(): void {
    if (!this.stats) {
      this.chartSeries = [];
      return;
    }

    const series = this.getActiveSeries();
    const categories = series.map((point) => this.formatLabel(point));

    this.chartSeries = this.buildSeries(series);
    this.chartOptions = {
      ...this.chartOptions,
      xaxis: {
        ...this.chartOptions.xaxis,
        categories,
      },
      legend: {
        ...this.chartOptions.legend,
        show: this.metric === 'all',
      },
      yaxis: this.buildYAxisOptions(series),
      colors: this.metric === 'profile' ? ['#b96300'] : this.metric === 'search' ? ['#2556cc'] : ['#2556cc', '#b96300'],
    };
  }

  private buildSeries(points: PhotographerViewStatPoint[]): ApexAxisChartSeries {
    const searchSeries = {
      name: this.searchLabel,
      data: points.map((point) => this.normalizeValue(point.searchImpressions)),
    };
    const profileSeries = {
      name: this.profileLabel,
      data: points.map((point) => this.normalizeValue(point.profileViews)),
    };

    switch (this.metric) {
      case 'search':
        return [searchSeries];
      case 'profile':
        return [profileSeries];
      default:
        return [searchSeries, profileSeries];
    }
  }

  private getActiveSeries(): PhotographerViewStatPoint[] {
    if (!this.stats) {
      return [];
    }
    const points = this.timeframe === 'daily' ? this.stats.daily : this.stats.monthly;
    if (this.timeframe === 'daily') {
      return points.slice(-30);
    }
    return points;
  }

  private formatLabel(point: PhotographerViewStatPoint): string {
    const date = new Date(point.date);
    if (Number.isNaN(date.getTime())) {
      return point.date;
    }
    if (this.timeframe === 'monthly') {
      return date.toLocaleDateString(undefined, {month: 'short', year: 'numeric'});
    }
    return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
  }

  private normalizeValue(value?: number | string | null): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private createBaseYAxisOptions(): ApexYAxis {
    return {
      min: 0,
      decimalsInFloat: 1,
      labels: {
        formatter: (value?: number) => this.formatYAxisValue(value),
      },
    };
  }

  private buildYAxisOptions(points: PhotographerViewStatPoint[]): ApexYAxis {
    const values = this.getVisibleValues(points);
    const maxValue = values.length ? Math.max(...values) : 0;
    const paddedMax = maxValue <= 1 ? 1 : Math.ceil(maxValue * 1.1);
    const tickAmount = Math.min(6, Math.max(2, Math.ceil(paddedMax)));

    return {
      ...this.createBaseYAxisOptions(),
      max: paddedMax,
      tickAmount,
    };
  }

  private getVisibleValues(points: PhotographerViewStatPoint[]): number[] {
    const values: number[] = [];

    for (const point of points) {
      const searchValue = this.normalizeValue(point.searchImpressions);
      const profileValue = this.normalizeValue(point.profileViews);

      switch (this.metric) {
        case 'search':
          values.push(searchValue);
          break;
        case 'profile':
          values.push(profileValue);
          break;
        default:
          values.push(searchValue, profileValue);
      }
    }

    return values.length ? values : [0];
  }

  private formatYAxisValue(value?: number): string {
    const normalized = Math.max(0, Number(value ?? 0));

    if (normalized === 0) {
      return '0';
    }

    if (normalized < 1) {
      return normalized.toFixed(1);
    }

    return Number.isInteger(normalized) ? normalized.toString() : normalized.toFixed(1);
  }
}
